import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import AppsIcon from '@material-ui/icons/Apps';
import CodeIcon from '@material-ui/icons/Code';
import StorageIcon from '@material-ui/icons/Storage';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import LinkIcon from '@material-ui/icons/Link';
import BuildIcon from '@material-ui/icons/Build';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { boschBlue, boschGray } from '../../modules/theme/boschColors';

type KindId =
  | 'group'
  | 'user'
  | 'system'
  | 'component'
  | 'api'
  | 'resource'
  | 'location'
  | 'template';

type Prereq =
  | { type: 'none'; explain?: string }
  | { type: 'kinds'; kinds: KindId[]; explain: string };

type KindInfo = {
  id: KindId;
  name: string;
  group: 'Software' | 'Organization' | 'Operational';
  icon: ReactNode;
  exampleEntityName: string;
  /** Sequential step in the recommended creation order. Same number = parallel/optional. */
  step: number;
  short: string;
  useCase: string;
  example: string;
  /** What MUST already exist for this entity to make sense. */
  prereq: Prereq;
  /** Fields the catalog validates as required on this kind. */
  required: string[];
  /** Other entities this kind ends up linked to once the catalog is fully wired. */
  recommended: string[];
};

const KINDS: KindInfo[] = [
  {
    id: 'group',
    name: 'Group',
    group: 'Organization',
    icon: <GroupIcon />,
    exampleEntityName: 'role-contributor',
    step: 1,
    short:
      'An RBAC group (role-grant) or department label. Only role-grant Groups grant permissions.',
    useCase:
      'Digital Factory uses three RBAC groups (role-admin, role-contributor, role-viewer) defined in catalog/org/role-grants.yaml. Users join exactly one RBAC group via spec.memberOf. The digital-factory department Group is for catalog ownership only (spec.owner).',
    example: `apiVersion: backstage.io/v1alpha1
kind: Group
metadata:
  name: role-contributor
spec:
  type: role-grant                   # role-grant = RBAC permission group
  profile:
    displayName: Developer
  children: []`,
    prereq: { type: 'none' },
    required: ['spec.type'],
    recommended: [
      'A matching g, row in rbac/policies.csv mapping this Group to a Casbin role',
      'User entities with this Group in spec.memberOf',
      'Software entities use group:digital-factory in spec.owner only (fixed ownership)',
    ],
  },
  {
    id: 'user',
    name: 'User',
    group: 'Organization',
    icon: <PersonIcon />,
    exampleEntityName: 'example-user-a',
    step: 1,
    short:
      'A person. Permissions come only from RBAC group membership (spec.memberOf).',
    useCase:
      'Each real person becomes a User entity. Sign-in matches profile.email to your identity. Assign exactly one role-grant Group — Platform Admin, Developer, or Read-only User — via Governance (/admin) or digital-factory-users.yaml.',
    example: `apiVersion: backstage.io/v1alpha1
kind: User
metadata:
  name: example-user-a
spec:
  profile:
    displayName: Example User A
    email: example-user-a@example.local
  memberOf:
    - role-contributor              # RBAC group only — permissions inherited`,
    prereq: {
      type: 'kinds',
      kinds: ['group'],
      explain:
        'The role-grant Group in memberOf must exist in catalog/org/role-grants.yaml.',
    },
    required: [],
    recommended: [
      'Exactly one RBAC group in memberOf (role-admin | role-contributor | role-viewer)',
      'No direct user→role grants (disabled in Governance by default)',
    ],
  },
  {
    id: 'system',
    name: 'System',
    group: 'Software',
    icon: <AccountTreeIcon />,
    exampleEntityName: 'example-system',
    step: 2,
    short:
      'A coherent group of Components, APIs and Resources that deliver one piece of business value. In our Digital Factory, think of a System as a "Project".',
    useCase: [
      '💡 In our Digital Factory, a System is what we call a "Project" —',
      'the umbrella that bundles every Component, API and Resource that',
      'ships together to deliver one piece of value. The YAML keeps',
      '`kind: System` (Backstage rejects custom kinds), but day-to-day',
      'you can read "System" as "Project" without losing anything.',
      '',
      'Examples in our org: BISB Auto Dash, Auto Flow Nexus, an internal',
      'scaffolder tool, a customer operations dashboard.',
      '',
      'Components, APIs and Resources point their `spec.system` at the',
      "System they belong to, which is what powers the System's",
      'diagram on the catalog detail page.',
    ].join('\n'),
    example: `apiVersion: backstage.io/v1alpha1
kind: System
metadata:
  name: example-system              # rename to your system
spec:
  owner: group:digital-factory`,
    prereq: {
      type: 'none',
      explain:
        'spec.owner is fixed to group:digital-factory (Digital Factory) — enforced by the catalog processor.',
    },
    required: ['spec.owner'],
    recommended: [
      'At least one Component / API / Resource pointing back via spec.system',
      'A clear metadata.description so newcomers understand what the System ships',
    ],
  },
  {
    id: 'component',
    name: 'Component',
    group: 'Software',
    icon: <AppsIcon />,
    exampleEntityName: 'example-backend-service',
    step: 3,
    short:
      'A piece of software you build, deploy and maintain — service, website, library, etc.',
    useCase:
      'Use for any concrete codebase. Each repo with a catalog-info.yaml usually maps to one Component.',
    example: `apiVersion: backstage.io/v1alpha1
kind: Component
metadata:
  name: example-backend-service     # rename to your repo
spec:
  type: service                     # service | website | library | ...
  lifecycle: production             # experimental | production | deprecated
  owner: group:digital-factory
  system: example-system
  providesApis: [example-rest-api]
  consumesApis: []
  dependsOn:
    - resource:default/example-database`,
    prereq: {
      type: 'kinds',
      kinds: ['system'],
      explain:
        'spec.owner is fixed to group:digital-factory. System is optional but, if you use it, must already exist — without it the Component has no place in the System diagram.',
    },
    required: [
      'spec.type',
      'spec.lifecycle',
      'spec.owner — always group:digital-factory (Digital Factory)',
    ],
    recommended: [
      'A System named in spec.system (e.g. example-system)',
      'API entities listed in providesApis / consumesApis',
      'Resource entities listed in dependsOn',
    ],
  },
  {
    id: 'api',
    name: 'API',
    group: 'Software',
    icon: <CodeIcon />,
    exampleEntityName: 'example-rest-api',
    step: 4,
    short:
      'A communication contract between components — REST, GraphQL, gRPC or AsyncAPI.',
    useCase:
      'Document any interface other Components depend on. The spec lives in the catalog so consumers can discover it.',
    example: `apiVersion: backstage.io/v1alpha1
kind: API
metadata:
  name: example-rest-api            # rename to your contract
spec:
  type: openapi                     # openapi | graphql | grpc | asyncapi
  lifecycle: production
  owner: group:digital-factory
  system: example-system
  definition:
    $text: ./openapi.yaml`,
    prereq: {
      type: 'kinds',
      kinds: ['component'],
      explain:
        'spec.owner is fixed to group:digital-factory. A Component that lists this API in providesApis should also exist — without one, the API is "orphan" and has no producer in the graph.',
    },
    required: [
      'spec.type',
      'spec.lifecycle',
      'spec.owner',
      'spec.definition (inline string or $text reference to a file)',
    ],
    recommended: [
      'A Component that lists this API in providesApis (otherwise the API is "orphan")',
      'A System matching the providing Component',
    ],
  },
  {
    id: 'resource',
    name: 'Resource',
    group: 'Software',
    icon: <StorageIcon />,
    exampleEntityName: 'example-database',
    step: 4,
    short:
      'Infrastructure or external dependency — database, queue, S3 bucket, terraform module.',
    useCase:
      'Anything a Component needs but is not your code. Great for tracking infra-as-code or managed services.',
    example: `apiVersion: backstage.io/v1alpha1
kind: Resource
metadata:
  name: example-database            # rename to your infra item
spec:
  type: database                    # database | s3-bucket | terraform-module | ...
  owner: group:digital-factory
  system: example-system
  dependencyOf:
    - component:default/example-backend-service`,
    prereq: {
      type: 'kinds',
      kinds: ['component'],
      explain:
        'spec.owner is fixed to group:digital-factory. A consuming Component should exist — otherwise the Resource just sits there with no incoming dependency.',
    },
    required: ['spec.type', 'spec.owner'],
    recommended: [
      'At least one Component listed in dependencyOf (or pointing here via dependsOn)',
      'A System matching the consuming Components',
    ],
  },
  {
    id: 'location',
    name: 'Location',
    group: 'Operational',
    icon: <LinkIcon />,
    exampleEntityName: 'example-location',
    step: 1,
    short:
      'Pointer to other catalog files (URL or path) so Backstage can discover them.',
    useCase:
      'Listed in app-config.yaml under catalog.locations to bootstrap the catalog. Rarely written by hand as a YAML file.',
    example: `apiVersion: backstage.io/v1alpha1
kind: Location
metadata:
  name: example-location            # rename to your bootstrap entry
spec:
  type: url                         # url | file | dir
  target: https://github.com/example-org/example-repo/blob/main/catalog-info.yaml
  targets:
    - ./catalog/components/example-backend-service.yaml
    - ./catalog/apis/example-rest-api.yaml
    - ./catalog/resources/example-database.yaml`,
    prereq: { type: 'none' },
    required: ['spec.target OR spec.targets'],
    recommended: [
      'Each target points to a valid catalog YAML',
      'Referenced YAMLs declare their own owner Groups',
    ],
  },
  {
    id: 'template',
    name: 'Template',
    group: 'Operational',
    icon: <BuildIcon />,
    exampleEntityName: 'example-template',
    step: 2,
    short:
      'A scaffolder recipe: parameters + steps that generate new repos, projects or modules.',
    useCase:
      'Standardize the creation of new services. Picked from the "Create…" page in Backstage.',
    example: `apiVersion: scaffolder.backstage.io/v1beta3
kind: Template
metadata:
  name: example-template            # rename to your generator
  title: Example service template
spec:
  type: service
  owner: group:digital-factory
  parameters:
    - title: Basics
      required: [name]
      properties:
        name: { type: string }
  steps:
    - id: fetch
      action: fetch:template
      input:
        url: ./skeleton
    - id: publish
      action: publish:azure
      input:
        repoUrl: dev.azure.com?...`,
    prereq: {
      type: 'none',
      explain:
        'spec.owner is fixed to group:digital-factory. Do not add OwnerPicker to template parameters.',
    },
    required: ['spec.type', 'spec.owner', 'spec.steps'],
    recommended: [
      'Hard-code group:digital-factory in generated catalog-info.yaml skeletons',
      'Output Components/repos created in spec.output get registered as new entities',
    ],
  },
];

const KINDS_BY_ID: Record<KindId, { kind: KindInfo; index: number }> =
  Object.fromEntries(
    KINDS.map((k, i) => [k.id, { kind: k, index: i }]),
  ) as Record<KindId, { kind: KindInfo; index: number }>;

const useStyles = makeStyles(theme => ({
  // ---- Diagram (vertical flow with numbered steps) ----
  diagramWrap: {
    border: `1px solid ${boschGray[85]}`,
    borderRadius: 6,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#FAFBFC',
  },
  diagramTitle: {
    fontSize: '0.85rem',
    color: boschGray[20],
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  diagramSubtitle: {
    fontSize: '0.75rem',
    color: boschGray[50],
    marginBottom: theme.spacing(2),
  },
  flowColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  stepRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  arrow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: boschBlue[50],
    fontSize: '0.75rem',
    fontWeight: 600,
    margin: theme.spacing(0.5, 0),
  },
  arrowIcon: {
    fontSize: 22,
  },
  stepCard: {
    display: 'flex',
    alignItems: 'stretch',
    border: `1.5px solid ${boschBlue[80]}`,
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    width: '100%',
    maxWidth: 520,
    overflow: 'hidden',
    '&:hover': {
      borderColor: boschBlue[50],
      boxShadow: theme.shadows[2],
      transform: 'translateY(-1px)',
    },
  },
  stepNumber: {
    width: 48,
    backgroundColor: boschBlue[50],
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  stepBody: {
    padding: theme.spacing(1, 1.5),
    flex: 1,
    minWidth: 0,
  },
  stepTopLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.65rem',
    color: boschBlue[40],
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 700,
  },
  stepName: {
    fontFamily: 'Consolas, Monaco, monospace',
    fontSize: '0.95rem',
    fontWeight: 700,
    color: boschGray[15],
    marginTop: 2,
  },
  stepNeeds: {
    fontSize: '0.75rem',
    color: boschGray[40],
    marginTop: 4,
    fontStyle: 'italic',
  },
  stepNeedsNone: {
    color: '#1B7F3B',
    fontWeight: 600,
    fontStyle: 'normal',
  },
  // Pair row (when two kinds live at the same step level)
  pairRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    width: '100%',
    maxWidth: 520,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  pairCard: {
    flex: '1 1 220px',
  },
  // Side branches (User, Operational)
  sideHeading: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: `1px dashed ${boschGray[80]}`,
    fontSize: '0.75rem',
    color: boschGray[40],
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: theme.spacing(1),
  },
  sideRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  // ---- Tabs / detail ----
  tabs: {
    minHeight: 48,
    borderBottom: `1px solid ${boschGray[85]}`,
    marginBottom: theme.spacing(2),
  },
  tab: {
    minHeight: 48,
    minWidth: 'auto',
    padding: theme.spacing(0.5, 2),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: boschGray[40],
    '&.Mui-selected': {
      color: boschBlue[50],
    },
  },
  indicator: {
    backgroundColor: boschBlue[50],
    height: 3,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  iconBox: {
    width: 44,
    height: 44,
    borderRadius: 4,
    backgroundColor: boschBlue[95],
    color: boschBlue[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: boschGray[15],
    lineHeight: 1.1,
  },
  groupChip: {
    height: 20,
    fontSize: '0.65rem',
    letterSpacing: 1,
    textTransform: 'uppercase',
    backgroundColor: boschBlue[95],
    color: boschBlue[40],
    fontWeight: 700,
  },
  short: {
    fontSize: '0.95rem',
    color: boschGray[20],
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  sectionLabel: {
    fontSize: '0.7rem',
    color: boschGray[50],
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  body: {
    fontSize: '0.9rem',
    color: boschGray[25],
    lineHeight: 1.5,
  },
  codeBlock: {
    backgroundColor: boschGray[95],
    border: `1px solid ${boschGray[85]}`,
    borderLeft: `3px solid ${boschBlue[50]}`,
    borderRadius: 4,
    padding: theme.spacing(1.5, 2),
    fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    fontSize: '0.8rem',
    color: boschGray[15],
    whiteSpace: 'pre',
    overflowX: 'auto',
    margin: 0,
  },
  block: {
    marginBottom: theme.spacing(2),
  },
  // Prerequisites — yellow accent so it really stands out
  prereqBox: {
    border: `1px solid #F2C94C`,
    borderLeft: `4px solid #F2994A`,
    borderRadius: 4,
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#FFF8E5',
    marginBottom: theme.spacing(2),
  },
  prereqHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '0.7rem',
    color: '#8C6D1F',
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  prereqList: {
    margin: theme.spacing(0.5, 0),
    paddingLeft: theme.spacing(2.5),
    fontSize: '0.85rem',
    color: boschGray[15],
    lineHeight: 1.6,
    '& li': {
      marginBottom: theme.spacing(0.25),
    },
  },
  prereqExplain: {
    fontSize: '0.8rem',
    color: boschGray[30],
    fontStyle: 'italic',
    marginTop: theme.spacing(0.5),
  },
  prereqNone: {
    fontSize: '0.85rem',
    color: '#1B7F3B',
    fontWeight: 600,
  },
  prereqLink: {
    color: boschBlue[40],
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 600,
    background: 'transparent',
    border: 'none',
    padding: 0,
    font: 'inherit',
  },
  listBox: {
    border: `1px solid ${boschGray[85]}`,
    borderLeft: `3px solid ${boschBlue[50]}`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    backgroundColor: boschGray[95],
  },
  recommendedBox: {
    border: `1px solid ${boschBlue[80]}`,
    borderLeft: `3px solid ${boschBlue[50]}`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    backgroundColor: boschBlue[95],
  },
  bulletList: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    fontSize: '0.85rem',
    color: boschGray[25],
    lineHeight: 1.6,
    '& li': {
      marginBottom: theme.spacing(0.25),
    },
  },
  emptyNote: {
    fontSize: '0.85rem',
    color: boschGray[50],
    fontStyle: 'italic',
  },
  // ---- Mental model cheat sheet ----
  mentalModelWrap: {
    border: `1px solid ${boschGray[85]}`,
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
    backgroundColor: '#FFFFFF',
  },
  mentalModelHeader: {
    backgroundColor: boschBlue[95],
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${boschGray[85]}`,
  },
  mentalModelTitle: {
    fontSize: '0.85rem',
    color: boschGray[20],
    fontWeight: 700,
  },
  mentalModelSubtitle: {
    fontSize: '0.7rem',
    color: boschGray[50],
    marginTop: 2,
  },
  mentalModelTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.85rem',
  },
  mentalModelTh: {
    textAlign: 'left',
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.7rem',
    color: boschGray[40],
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 700,
    backgroundColor: boschGray[95],
    borderBottom: `1px solid ${boschGray[85]}`,
  },
  mentalModelTd: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${boschGray[90]}`,
    color: boschGray[20],
    verticalAlign: 'middle',
  },
  mentalModelTdLast: {
    borderBottom: 'none',
  },
  mentalModelKeyword: {
    fontWeight: 700,
    color: boschBlue[40],
    marginRight: 6,
  },
  mentalModelKindLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'Consolas, Monaco, monospace',
    fontSize: '0.85rem',
    fontWeight: 700,
    color: boschBlue[50],
    textDecoration: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

type MentalModelProps = {
  onJumpTo: (id: KindId) => void;
};

type MentalRow = {
  keyword: string;
  question: string;
  kindId: KindId;
};

const MENTAL_ROWS: MentalRow[] = [
  { keyword: 'WHO', question: 'has platform access?', kindId: 'user' },
  {
    keyword: 'WHAT',
    question: 'do we deliver as a bundle?',
    kindId: 'system',
  },
  {
    keyword: 'WHICH',
    question: 'concrete piece of code?',
    kindId: 'component',
  },
];

/**
 * RBAC-only permission model — matches Governance hub and rbac/policies.csv.
 */
const RbacResolutionModel = () => {
  const classes = useStyles();
  return (
    <Box className={classes.mentalModelWrap} style={{ marginBottom: 16 }}>
      <Box className={classes.mentalModelHeader}>
        <Typography className={classes.mentalModelTitle}>
          RBAC resolution model
        </Typography>
        <Typography className={classes.mentalModelSubtitle}>
          Permissions are always derived from RBAC group membership. No embedded
          squads. Direct user→role assignment is disabled in Governance (feature
          flag).
        </Typography>
      </Box>
      <pre
        className={classes.codeBlock}
        style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.5 }}
      >
        {`User.spec.memberOf  →  RBAC Group (role-grant)
        │                      role-admin | role-contributor | role-viewer
        ▼
rbac/policies.csv (g, rows)  →  Casbin role (admin | contributor | viewer | guest)
        ▼
Effective permissions  →  what the user can do in Backstage

Guest fallback: no role-grant Group → defaultRole in app-config.yaml`}
      </pre>
      <Typography className={classes.mentalModelSubtitle} style={{ marginTop: 8 }}>
        <strong>Roles define what you can do. Ownership defines who the system
        belongs to.</strong> They are completely independent. Assign users in{' '}
        <strong>Governance</strong> (<code>/admin</code>) or{' '}
        <code>catalog/org/digital-factory-users.yaml</code>. Catalog{' '}
        <code>spec.owner</code> is always <code>group:digital-factory</code>{' '}
        (Digital Factory) — enforced in the catalog processor and scaffolder UI.
      </Typography>
    </Box>
  );
};

/**
 * One-glance mental model: each row is a question and the kind that
 * answers it. Sits at the top of the guide so newcomers can grok the
 * Group / System / Component split before reading anything else.
 */
const MentalModel = ({ onJumpTo }: MentalModelProps) => {
  const classes = useStyles();
  return (
    <Box className={classes.mentalModelWrap}>
      <Box className={classes.mentalModelHeader}>
        <Typography className={classes.mentalModelTitle}>
          Mental model — what does each kind answer?
        </Typography>
        <Typography className={classes.mentalModelSubtitle}>
          Four questions, four kinds. Click any kind to jump to its tab.
        </Typography>
      </Box>
      <table className={classes.mentalModelTable}>
        <thead>
          <tr>
            <th className={classes.mentalModelTh}>Question</th>
            <th
              className={classes.mentalModelTh}
              style={{ width: 160 }}
            >
              Kind
            </th>
          </tr>
        </thead>
        <tbody>
          {MENTAL_ROWS.map((row, i) => {
            const isLast = i === MENTAL_ROWS.length - 1;
            const td = `${classes.mentalModelTd}${
              isLast ? ` ${classes.mentalModelTdLast}` : ''
            }`;
            const kind = KINDS_BY_ID[row.kindId].kind;
            return (
              <tr key={row.kindId}>
                <td className={td}>
                  <span className={classes.mentalModelKeyword}>
                    {row.keyword}
                  </span>
                  {row.question}
                </td>
                <td className={td}>
                  <button
                    type="button"
                    className={classes.mentalModelKindLink}
                    onClick={() => onJumpTo(row.kindId)}
                    title={`Jump to ${kind.name}`}
                  >
                    <span style={{ display: 'inline-flex', fontSize: 16 }}>
                      {kind.icon}
                    </span>
                    {kind.name}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

type StepCardProps = {
  kindId: KindId;
  onClick: (id: KindId) => void;
};

const StepCard = ({ kindId, onClick }: StepCardProps) => {
  const classes = useStyles();
  const { kind } = KINDS_BY_ID[kindId];
  const needsText =
    kind.prereq.type === 'none'
      ? 'No prerequisites — start here'
      : `Needs: ${kind.prereq.kinds
          .map(k => `#${KINDS_BY_ID[k].kind.step} ${KINDS_BY_ID[k].kind.name}`)
          .join(' + ')}`;

  return (
    <Box
      className={classes.stepCard}
      role="button"
      tabIndex={0}
      onClick={() => onClick(kindId)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onClick(kindId);
      }}
      title={`Click to see details for ${kind.name}`}
    >
      <Box className={classes.stepNumber}>{kind.step}</Box>
      <Box className={classes.stepBody}>
        <Box className={classes.stepTopLine}>
          <span style={{ display: 'inline-flex', fontSize: 12 }}>
            {kind.icon}
          </span>
          {kind.name}
          <Chip
            label={kind.group}
            size="small"
            style={{
              height: 16,
              fontSize: '0.6rem',
              marginLeft: 4,
            }}
          />
        </Box>
        <Typography className={classes.stepName}>
          {kind.exampleEntityName}
        </Typography>
        <Typography
          className={`${classes.stepNeeds} ${
            kind.prereq.type === 'none' ? classes.stepNeedsNone : ''
          }`}
        >
          {needsText}
        </Typography>
      </Box>
    </Box>
  );
};

const ArrowDown = ({ label }: { label: string }) => {
  const classes = useStyles();
  return (
    <Box className={classes.arrow}>
      <ArrowDownwardIcon className={classes.arrowIcon} />
      {label}
    </Box>
  );
};

type DiagramProps = {
  onNodeClick: (id: KindId) => void;
};

/**
 * Vertical creation-order diagram. Steps are numbered (1 → 4), each card
 * announces its prerequisites in plain English, and arrows between steps
 * say *why* you go from one to the next.
 *
 * The Software pyramid lives in the main flow. User and the operational
 * kinds (Location, Template) live in side branches because they don't
 * follow the Group → System → Component pipeline.
 */
const HierarchyDiagram = ({ onNodeClick }: DiagramProps) => {
  const classes = useStyles();
  return (
    <Box className={classes.diagramWrap}>
      <Typography className={classes.diagramTitle}>
        Recommended creation order
      </Typography>
      <Typography className={classes.diagramSubtitle}>
        Each numbered step depends on the one above. The dashed area below
        shows kinds that don't fit the main Software pyramid. Click any
        card to see its tab.
      </Typography>

      <Box className={classes.flowColumn}>
        <Box className={classes.stepRow}>
          <StepCard kindId="group" onClick={onNodeClick} />
        </Box>

        <ArrowDown label="every entity below needs an owner Group" />

        <Box className={classes.stepRow}>
          <StepCard kindId="system" onClick={onNodeClick} />
        </Box>

        <ArrowDown label="Components live inside a System" />

        <Box className={classes.stepRow}>
          <StepCard kindId="component" onClick={onNodeClick} />
        </Box>

        <ArrowDown label="Components provide APIs and depend on Resources" />

        <Box className={classes.pairRow}>
          <Box className={classes.pairCard}>
            <StepCard kindId="api" onClick={onNodeClick} />
          </Box>
          <Box className={classes.pairCard}>
            <StepCard kindId="resource" onClick={onNodeClick} />
          </Box>
        </Box>
      </Box>

      <Typography className={classes.sideHeading}>
        Side branches — independent of the pyramid above
      </Typography>
      <Box className={classes.sideRow}>
        <Box className={classes.pairCard}>
          <StepCard kindId="user" onClick={onNodeClick} />
        </Box>
        <Box className={classes.pairCard}>
          <StepCard kindId="location" onClick={onNodeClick} />
        </Box>
        <Box className={classes.pairCard}>
          <StepCard kindId="template" onClick={onNodeClick} />
        </Box>
      </Box>
    </Box>
  );
};

type PrerequisitesProps = {
  prereq: Prereq;
  onJumpTo: (id: KindId) => void;
};

const Prerequisites = ({ prereq, onJumpTo }: PrerequisitesProps) => {
  const classes = useStyles();

  if (prereq.type === 'none') {
    return (
      <Box className={classes.prereqBox}>
        <Typography className={classes.prereqHeader}>
          ⚑ Prerequisites
        </Typography>
        <Typography className={classes.prereqNone}>
          None — you can create this entity first, with no other entities
          in the catalog.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.prereqBox}>
      <Typography className={classes.prereqHeader}>
        ⚑ Prerequisites — these MUST exist first
      </Typography>
      <ul className={classes.prereqList}>
        {prereq.kinds.map(k => {
          const target = KINDS_BY_ID[k].kind;
          return (
            <li key={k}>
              <button
                type="button"
                className={classes.prereqLink}
                onClick={() => onJumpTo(k)}
              >
                #{target.step} {target.name}
              </button>{' '}
              <span style={{ color: '#5B5B5B' }}>
                (e.g. <code>{target.exampleEntityName}</code>)
              </span>
            </li>
          );
        })}
      </ul>
      <Typography className={classes.prereqExplain}>
        {prereq.explain}
      </Typography>
    </Box>
  );
};

export const CatalogKindsGuide = () => {
  const classes = useStyles();
  const [active, setActive] = useState(0);
  const kind = KINDS[active];

  const jumpTo = useMemo(
    () => (id: KindId) => {
      setActive(KINDS_BY_ID[id].index);
    },
    [],
  );

  return (
    <InfoCard
      title="Catalog kinds — what each one means"
      subheader="Numbered creation order, with prerequisites called out for every kind. Click a card to see its details."
    >
      <RbacResolutionModel />
      <MentalModel onJumpTo={jumpTo} />
      <HierarchyDiagram onNodeClick={jumpTo} />

      <Tabs
        value={active}
        onChange={(_, v) => setActive(v)}
        variant="scrollable"
        scrollButtons="auto"
        classes={{ root: classes.tabs, indicator: classes.indicator }}
      >
        {KINDS.map(k => (
          <Tab
            key={k.id}
            label={`#${k.step} ${k.name}`}
            icon={
              <span style={{ display: 'inline-flex', marginRight: 4 }}>
                {k.icon}
              </span>
            }
            classes={{ root: classes.tab }}
          />
        ))}
      </Tabs>

      <Box className={classes.header}>
        <Box className={classes.iconBox}>{kind.icon}</Box>
        <Box flex={1}>
          <Typography className={classes.title}>
            #{kind.step} {kind.name}
          </Typography>
          <Chip
            label={kind.group}
            size="small"
            className={classes.groupChip}
          />
        </Box>
      </Box>

      <Typography className={classes.short}>{kind.short}</Typography>

      <Prerequisites prereq={kind.prereq} onJumpTo={jumpTo} />

      <Box className={classes.block}>
        <Typography className={classes.sectionLabel}>When to use</Typography>
        <Typography className={classes.body}>{kind.useCase}</Typography>
      </Box>

      <Box className={classes.block}>
        <Typography className={classes.sectionLabel}>Example</Typography>
        <pre className={classes.codeBlock}>{kind.example}</pre>
      </Box>

      <Box className={classes.block}>
        <Typography className={classes.sectionLabel}>
          Required fields (catalog will reject otherwise)
        </Typography>
        {kind.required.length === 0 ? (
          <Box className={classes.listBox}>
            <Typography className={classes.emptyNote}>
              None — only the standard apiVersion + kind + metadata.name.
            </Typography>
          </Box>
        ) : (
          <Box className={classes.listBox}>
            <ul className={classes.bulletList}>
              {kind.required.map(r => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </Box>
        )}
      </Box>

      <Box>
        <Typography className={classes.sectionLabel}>
          Recommended relations (entities that should already exist)
        </Typography>
        <Box className={classes.recommendedBox}>
          <ul className={classes.bulletList}>
            {kind.recommended.map(r => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </Box>
      </Box>
    </InfoCard>
  );
};
